/**
 * =====================================================
 * RESERVATION APPLICATION
 * 4-Step Flow: Your details → Date & time → Menu → Checkout
 * =====================================================
 * Step 1: Contact details + party size
 * Step 2: Date & time
 * Step 3: Menu selection (same Menu API as takeout)
 * Step 4: Summary & payment
 */

const ReservationApp = (function() {
  'use strict';

  // ============== STATE ==============
  let currentStep = 1;
  let selectedSlot = null;     // { date, time }
  let slotsForDate = [];       // available times for the chosen date
  let slotsRequest = 0;        // ignores answers for a date the guest has since changed
  let menuData = null;
  let cart = [];
  let activeCategory = 'all';
  let activeFilter = 'all';
  let searchQuery = '';
  let stripe = null;
  let cardElement = null;
  let walletElements = null; // Apple Pay / Google Pay buttons
  let walletAmount = 0;      // total shown in the Apple Pay / Google Pay sheet

  // Reservation created on the server for the current checkout attempt.
  // Re-used on retries so a failed attempt never creates a second
  // reservation or a second charge: { key, reservationId, totals, paid }
  let pendingReservation = null;

  // ============== CONFIG ==============
  const config = window.ReservationConfig || {};

  // Booking rules and pricing. These defaults are replaced by the
  // Reservation API's getSettings — the API is the source of truth.
  const settings = {
    today: null,
    closedDays: [1],
    timeSlots: [],
    minGuests: 2,
    maxGuests: 12,
    maxAdvanceDays: 30,
    taxPercent: 6,
    gratuityPercent: 15
  };

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // ============== INITIALIZATION ==============

  function init() {
    console.log('ReservationApp: Initializing...');

    setupEventListeners();

    // Initialize Stripe if needed
    if (config.showPayment && config.stripe?.publishableKey) {
      initStripe();
    }

    loadSettings();

    console.log('ReservationApp: Ready');
  }

  /**
   * Load closed days, times, party size limits, tax and gratuity from
   * the Reservation API, then show the form. If the call fails the
   * defaults are used — the API still checks everything on booking.
   */
  async function loadSettings() {
    if (config.reservationApiUrl) {
      try {
        const data = await getJson(`${config.reservationApiUrl}?action=getSettings`);
        if (data.success && data.settings) {
          Object.assign(settings, data.settings);
        }
      } catch (err) {
        console.warn('ReservationApp: Could not load settings, using defaults.', err);
      }
    }

    applySettings();

    const loadingEl = document.getElementById('reservation-loading');
    const mainContent = document.getElementById('reservation-main-content');
    if (loadingEl) loadingEl.style.display = 'none';
    if (mainContent) mainContent.style.display = 'block';
  }

  function applySettings() {
    document.querySelectorAll('[data-tax-percent]').forEach(el => { el.textContent = settings.taxPercent; });
    document.querySelectorAll('[data-gratuity-percent]').forEach(el => { el.textContent = settings.gratuityPercent; });

    // Party size options
    const guestsSelect = document.getElementById('reservation-guests');
    if (guestsSelect) {
      const current = guestsSelect.value;
      let options = '<option value="">Choose party size</option>';
      for (let n = settings.minGuests; n <= settings.maxGuests; n++) {
        options += `<option value="${n}">${n} guests</option>`;
      }
      guestsSelect.innerHTML = options;
      if (current) guestsSelect.value = current;
    }

    // Bookable date range, in restaurant time
    const dateInput = document.getElementById('reservation-date');
    if (dateInput) {
      const today = settings.today || localToday();
      dateInput.min = today;
      dateInput.max = addDays(today, settings.maxAdvanceDays);
    }

    // "Reservations: Tue–Sun, 6:00 PM – 9:00 PM" under the page title
    const hoursEl = document.getElementById('reservation-hours');
    if (hoursEl && settings.timeSlots.length) {
      const first = settings.timeSlots[0];
      const last = settings.timeSlots[settings.timeSlots.length - 1];
      hoursEl.textContent = `Reservations: ${describeOpenDays()}, ${formatTime(first)} – ${formatTime(last)}`;
      hoursEl.hidden = false;
    }
  }

  function setupEventListeners() {
    // Date + party size
    document.getElementById('reservation-date')?.addEventListener('change', handleDateChange);
    document.getElementById('reservation-guests')?.addEventListener('change', () => {
      if (selectedSlot || slotsForDate.length) renderTimeSlots();
    });

    // Menu search
    const searchInput = document.getElementById('menu-search');
    if (searchInput) {
      searchInput.addEventListener('input', debounce(handleSearch, 300));
    }

    // Search clear
    const searchClear = document.getElementById('search-clear');
    if (searchClear) {
      searchClear.addEventListener('click', clearSearch);
    }

    // Dietary filters
    document.querySelectorAll('.filter-tag').forEach(tag => {
      tag.addEventListener('click', () => handleFilterClick(tag));
    });

    // Escape closes the mobile order sheet
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeMobileCart();
    });
  }


  // ============== STEP NAVIGATION ==============

  function goToStep(step) {
    // Validate before moving forward
    if (step > currentStep) {
      if (!validateCurrentStep()) {
        return;
      }
    }

    // Party size may have changed — refresh which times fit
    if (step === 2 && slotsForDate.length) {
      renderTimeSlots();
    }

    // Load the menu the first time the guest reaches it
    if (step === 3 && !menuData) {
      loadMenu();
    }

    // Populate summary when entering step 4
    if (step === 4) {
      populateSummary();
    }

    // The mobile cart drawer lives in step 3 — close it (and its page
    // overlay) so it can't cover the next step
    closeMobileCart();

    // Hide all steps
    document.querySelectorAll('.form-step').forEach(el => {
      el.style.display = 'none';
    });

    // Show target step
    const targetStep = document.querySelector(`[data-form-step="${step}"]`);
    if (targetStep) {
      targetStep.style.display = 'block';
    }

    // Update step indicators
    document.querySelectorAll('.step').forEach(el => {
      const stepNum = parseInt(el.dataset.step);
      el.classList.remove('active', 'completed');

      if (stepNum < step) {
        el.classList.add('completed');
      } else if (stepNum === step) {
        el.classList.add('active');
      }
    });

    // Update step lines
    document.querySelectorAll('.step-line').forEach(el => {
      const lineNum = parseInt(el.dataset.line);
      el.classList.toggle('completed', lineNum < step);
    });

    currentStep = step;
    hideMessage();

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function validateCurrentStep() {
    if (currentStep === 1) {
      return validateStep1();
    } else if (currentStep === 2) {
      return validateStep2();
    } else if (currentStep === 3) {
      return validateStep3();
    }
    return true;
  }

  function validateStep1() {
    const name = document.getElementById('reservation-name')?.value.trim();
    const email = document.getElementById('reservation-email')?.value.trim();
    const phone = document.getElementById('reservation-phone')?.value.trim();

    if (!name || !email || !phone) {
      showMessage(config.messages?.fillRequired || 'Please fill in all required fields', 'error');
      return false;
    }

    if (!isValidEmail(email)) {
      showMessage(config.messages?.invalidEmail || 'Please enter a valid email address', 'error');
      return false;
    }

    const guests = getGuestCount();
    if (!(guests >= settings.minGuests && guests <= settings.maxGuests)) {
      showMessage(config.messages?.chooseGuests || 'Please choose your party size', 'error');
      return false;
    }

    return true;
  }

  function validateStep2() {
    if (!selectedSlot) {
      showMessage(config.messages?.selectTime || 'Please choose a date and time', 'error');
      return false;
    }
    return true;
  }

  function validateStep3() {
    if (cart.length === 0) {
      showMessage(config.messages?.emptyCart || 'Please add at least one dish to your menu', 'error');
      return false;
    }
    return true;
  }


  // ============== STEP 2: DATE & TIME ==============

  function handleDateChange(e) {
    const date = e.target.value;

    selectedSlot = null;
    slotsForDate = [];
    hideMessage();

    if (!date) {
      renderSlotsMessage('Choose a date to see available times.');
      return;
    }

    // Quick check for closed days — the API checks again
    const dayOfWeek = getDayOfWeek(date);
    if (settings.closedDays.includes(dayOfWeek)) {
      slotsRequest++;
      renderSlotsMessage(`We're closed on ${DAY_NAMES[dayOfWeek]}s. Please choose another date.`);
      return;
    }

    loadTimeSlots(date);
  }

  async function loadTimeSlots(date) {
    const requestId = ++slotsRequest;
    const loading = document.getElementById('slots-loading');
    const container = document.getElementById('time-slots');

    container.innerHTML = '';
    loading.hidden = false;
    updateTimeContinue();

    try {
      const data = await getJson(`${config.reservationApiUrl}?action=getAvailableSlots&date=${encodeURIComponent(date)}`);
      if (requestId !== slotsRequest) return;

      if (!data.success) {
        throw new Error(data.error || 'We could not load the available times.');
      }

      slotsForDate = (data.slots || []).map(slot => ({ ...slot, date: date }));

      if (slotsForDate.length) {
        renderTimeSlots();
      } else {
        renderSlotsMessage(data.message || 'There are no tables left on this date. Please choose another date.');
      }
    } catch (error) {
      if (requestId !== slotsRequest) return;
      console.error('Error loading times:', error);
      renderSlotsMessage(`${error.message || 'We could not load the available times.'} Please try again.`, true);
    } finally {
      if (requestId === slotsRequest) loading.hidden = true;
    }
  }

  /** Time buttons for the chosen date; times without room for the party are disabled */
  function renderTimeSlots() {
    const container = document.getElementById('time-slots');
    const guests = getGuestCount();

    container.innerHTML = '';

    slotsForDate.forEach(slot => {
      const fits = !guests || slot.remainingCapacity >= guests;
      const isSelected = fits && selectedSlot?.date === slot.date && selectedSlot?.time === slot.time;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'time-slot-btn' + (isSelected ? ' selected' : '');
      btn.disabled = !fits;
      btn.setAttribute('aria-pressed', String(isSelected));
      btn.innerHTML = `
        <span class="slot-time">${escapeHtml(formatTime(slot.time))}</span>
        <span class="slot-seats">${fits ? `${slot.remainingCapacity} seats left` : 'Not enough seats'}</span>
      `;
      btn.addEventListener('click', () => selectTimeSlot(slot));
      container.appendChild(btn);
    });

    // Drop a chosen time that no longer fits the party
    if (selectedSlot && !slotsForDate.some(s => s.date === selectedSlot.date && s.time === selectedSlot.time && (!guests || s.remainingCapacity >= guests))) {
      selectedSlot = null;
    }

    updateTimeContinue();
  }

  function selectTimeSlot(slot) {
    selectedSlot = { date: slot.date, time: slot.time };
    renderTimeSlots();
    hideMessage();
  }

  function renderSlotsMessage(text, isError) {
    const container = document.getElementById('time-slots');
    container.innerHTML = `<p class="time-slots-placeholder${isError ? ' is-error' : ''}">${escapeHtml(text)}</p>`;
    selectedSlot = null;
    updateTimeContinue();
  }

  function updateTimeContinue() {
    const btn = document.getElementById('continue-to-menu-btn');
    if (btn) btn.disabled = !selectedSlot;
  }


  // ============== STEP 3: MENU SELECTION ==============

  async function loadMenu() {
    const loading = document.getElementById('menu-loading');
    const errorEl = document.getElementById('menu-error');
    const grid = document.getElementById('menu-items-grid');

    loading.style.display = 'flex';
    errorEl.style.display = 'none';
    grid.style.display = 'none';

    try {
      const data = await getJson(`${config.menuApiUrl}?action=getMenu`);

      if (!data.success) {
        throw new Error(data.error || 'Failed to load menu');
      }

      menuData = data.data;
      console.log('Menu loaded:', menuData);

      renderCategories();
      renderMenuItems();
      updateCartUI();

      loading.style.display = 'none';
      grid.style.display = 'block';

    } catch (error) {
      console.error('Error loading menu:', error);
      loading.style.display = 'none';
      errorEl.style.display = 'flex';
      document.getElementById('menu-error-text').textContent = error.message;
    }
  }

  function renderCategories() {
    const container = document.getElementById('category-tabs');
    if (!container || !menuData?.categories) return;

    const inner = container.querySelector('.category-tabs-inner');
    inner.innerHTML = '';
    activeCategory = 'all';

    // All tab
    const allTab = createCategoryTab({ category: 'all', display_name: 'All', icon: '🍽️' });
    allTab.classList.add('active');
    inner.appendChild(allTab);

    // Category tabs
    menuData.categories.forEach(cat => {
      inner.appendChild(createCategoryTab(cat));
    });
  }

  function createCategoryTab(cat) {
    const template = document.getElementById('category-tab-template');
    const clone = template.content.cloneNode(true);
    const btn = clone.querySelector('.category-tab');

    btn.dataset.category = cat.category;
    btn.querySelector('.category-icon').textContent = cat.icon || '🍴';
    btn.querySelector('.category-name').textContent = cat.display_name || cat.category;

    btn.addEventListener('click', () => {
      document.querySelectorAll('.category-tab').forEach(t => t.classList.remove('active'));
      btn.classList.add('active');
      activeCategory = cat.category;
      renderMenuItems();

      // If the tabs are pinned while scrolling, jump back to the start of the menu
      const menuSection = document.querySelector('.menu-section');
      if (menuSection && menuSection.getBoundingClientRect().top < 0) {
        menuSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    return btn;
  }

  /**
   * Render the menu grouped by category (in the Categories sheet order),
   * each group under its own heading — like the printed menu.
   */
  function renderMenuItems() {
    const grid = document.getElementById('menu-items-grid');
    const noResults = document.getElementById('no-results');

    if (!grid || !menuData?.allItems) return;

    grid.innerHTML = '';

    let items = getFilteredItems();

    if (items.length === 0) {
      grid.style.display = 'none';
      noResults.style.display = 'flex';
      return;
    }

    grid.style.display = 'block';
    noResults.style.display = 'none';

    const categories = menuData.categories || [];
    const categoryOrder = categories.map(c => c.category);
    const rank = name => {
      const i = categoryOrder.indexOf(name);
      return i === -1 ? categoryOrder.length : i;
    };
    const displayName = name => {
      const cat = categories.find(c => c.category === name);
      return String((cat && cat.display_name) || name || 'More').trim();
    };

    const groups = new Map();
    items.forEach(item => {
      if (!groups.has(item.category)) groups.set(item.category, []);
      groups.get(item.category).push(item);
    });

    [...groups.keys()].sort((a, b) => rank(a) - rank(b)).forEach(category => {
      const group = document.createElement('section');
      group.className = 'menu-group';

      const heading = document.createElement('h3');
      heading.className = 'menu-group-title';
      heading.textContent = displayName(category);
      group.appendChild(heading);

      // MLCC: guests ordering alcohol must show picture ID
      if (groups.get(category).some(item => item.alcohol === true)) {
        const notice = document.createElement('p');
        notice.className = 'menu-group-notice';
        notice.textContent = config.messages?.alcoholIdNotice ||
          'As per MLCC guidelines, picture ID must be present for age verification before service. If ID can\'t be verified, drinks are refunded minus a 4% administration fee.';
        group.appendChild(notice);
      }

      groups.get(category).forEach(item => group.appendChild(createMenuItemCard(item)));
      grid.appendChild(group);
    });

    updateCartIndicators();
  }

  function getFilteredItems() {
    if (!menuData?.allItems) return [];

    let items = [...menuData.allItems];

    // Category filter
    if (activeCategory !== 'all') {
      items = items.filter(i => i.category === activeCategory);
    }

    // Dietary filter
    if (activeFilter !== 'all') {
      items = items.filter(i => i.dietary_tags?.includes(activeFilter));
    }

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i =>
        i.name.toLowerCase().includes(q) ||
        (i.description && i.description.toLowerCase().includes(q))
      );
    }

    return items;
  }

  function createMenuItemCard(item) {
    const template = document.getElementById('menu-item-template');
    const clone = template.content.cloneNode(true);
    const card = clone.querySelector('.menu-item-card');

    card.dataset.itemId = item.id;
    card.dataset.category = item.category;
    card.dataset.tags = (item.dietary_tags || []).join(',');

    // Content
    card.querySelector('.menu-item-name').textContent = item.name;
    card.querySelector('.menu-item-price').textContent = formatCurrency(item.price);
    card.querySelector('.menu-item-description').textContent = item.description || '';

    // Tags
    const tagsEl = card.querySelector('.menu-item-tags');
    if (item.dietary_tags?.length) {
      tagsEl.innerHTML = item.dietary_tags.map(tag => {
        const slug = String(tag).toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return `<span class="tag tag-${escapeHtml(slug)}">${escapeHtml(formatTag(tag))}</span>`;
      }).join('');
    }

    // Spice selector — only the levels listed for this item on the menu sheet
    const spiceEl = card.querySelector('.menu-item-spice');
    const spiceOptions = getSpiceOptions(item);
    if (spiceOptions.length) {
      spiceEl.innerHTML = `
        <div class="menu-spice-selector" role="radiogroup" aria-label="Spice level for ${escapeHtml(item.name)}">
          <span class="menu-spice-label" aria-hidden="true">Spice</span>
          <div class="menu-spice-options">
            ${spiceOptions.map((level, i) => `
              <label class="menu-spice-option">
                <input type="radio" name="spice-${escapeHtml(item.id)}" value="${escapeHtml(level)}"${i === 0 ? ' checked' : ''}>
                <span class="menu-spice-badge">${escapeHtml(formatSpice(level))}</span>
              </label>
            `).join('')}
          </div>
        </div>
      `;
      card.dataset.hasSpice = 'true';
    }

    // Sides
    const sidesEl = card.querySelector('.menu-item-sides');
    if (item.sides && item.sides.length > 0) {
      sidesEl.innerHTML = `
        <div class="menu-sides-selector" role="radiogroup" aria-label="Side for ${escapeHtml(item.name)}">
          <span class="menu-sides-label" aria-hidden="true">Side</span>
          <div class="menu-sides-options">
            ${item.sides.map((side, i) => `
              <label class="menu-side-option">
                <input type="radio" name="side-${escapeHtml(item.id)}" value="${escapeHtml(side)}"${i === 0 ? ' checked' : ''}>
                <span class="menu-side-badge">${escapeHtml(side)}</span>
              </label>
            `).join('')}
          </div>
        </div>
      `;
      card.dataset.hasSides = 'true';
    }

    return card;
  }

  function handleFilterClick(btn) {
    const isActive = btn.classList.contains('active');

    document.querySelectorAll('.filter-tag').forEach(t => t.classList.remove('active'));

    if (!isActive) {
      btn.classList.add('active');
      activeFilter = btn.dataset.filter;
    } else {
      document.querySelector('.filter-tag[data-filter="all"]')?.classList.add('active');
      activeFilter = 'all';
    }

    renderMenuItems();
  }

  function handleSearch(e) {
    searchQuery = e.target.value.trim();
    document.getElementById('search-clear').style.display = searchQuery ? 'block' : 'none';
    renderMenuItems();
  }

  function clearSearch() {
    document.getElementById('menu-search').value = '';
    searchQuery = '';
    document.getElementById('search-clear').style.display = 'none';
    renderMenuItems();
  }

  function clearFilters() {
    activeCategory = 'all';
    activeFilter = 'all';
    searchQuery = '';

    document.querySelectorAll('.category-tab').forEach(t => t.classList.remove('active'));
    document.querySelector('.category-tab[data-category="all"]')?.classList.add('active');

    document.querySelectorAll('.filter-tag').forEach(t => t.classList.remove('active'));
    document.querySelector('.filter-tag[data-filter="all"]')?.classList.add('active');

    document.getElementById('menu-search').value = '';
    document.getElementById('search-clear').style.display = 'none';

    renderMenuItems();
  }


  // ============== CART MANAGEMENT ==============

  function addToCart(btn) {
    const card = btn.closest('.menu-item-card');
    const itemId = card.dataset.itemId;
    const item = menuData.allItems.find(i => i.id === itemId);

    if (!item) return;

    // Get selected spice level (defaults to the first listed level)
    const spiceOptions = getSpiceOptions(item);
    let selectedSpice = null;
    if (spiceOptions.length) {
      const checkedSpice = card.querySelector('.menu-item-spice input[type="radio"]:checked');
      selectedSpice = checkedSpice && spiceOptions.includes(checkedSpice.value) ? checkedSpice.value : spiceOptions[0];
    }

    // Get selected side
    let selectedSide = null;
    if (item.sides && item.sides.length > 0) {
      const checkedRadio = card.querySelector('.menu-item-sides input[type="radio"]:checked');
      selectedSide = checkedRadio ? checkedRadio.value : item.sides[0];
    }

    const existing = cart.find(c => c.id === itemId);

    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1,
        spiceLevel: selectedSpice,
        selectedSide: selectedSide
      });
    }

    updateCartUI();
    updateCartIndicators();
  }

  function updateCartItem(btn, delta) {
    const card = btn.closest('.menu-item-card');
    changeQuantity(card.dataset.itemId, delta);
  }

  function updateCartItemFromCart(btn, delta) {
    const cartItem = btn.closest('.cart-item');
    changeQuantity(cartItem.dataset.itemId, delta);
  }

  function changeQuantity(itemId, delta) {
    const item = cart.find(c => c.id === itemId);
    if (!item) return;

    item.quantity += delta;

    if (item.quantity <= 0) {
      cart = cart.filter(c => c.id !== itemId);
    }

    updateCartUI();
    updateCartIndicators();
  }

  function removeFromCart(btn) {
    const cartItem = btn.closest('.cart-item');
    const itemId = cartItem.dataset.itemId;

    cart = cart.filter(c => c.id !== itemId);

    updateCartUI();
    updateCartIndicators();
  }

  function clearCart() {
    if (!confirm('Remove all dishes from your menu?')) return;
    cart = [];
    updateCartUI();
    updateCartIndicators();
  }

  function getCartTotals() {
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    const tax = Math.round(subtotal * ((parseFloat(settings.taxPercent) || 0) / 100));
    const gratuity = Math.round(subtotal * ((parseFloat(settings.gratuityPercent) || 0) / 100));
    const total = subtotal + tax + gratuity;

    return { subtotal, tax, gratuity, total, itemCount };
  }

  function updateCartUI() {
    const totals = getCartTotals();
    const isEmpty = cart.length === 0;

    // Counts
    document.getElementById('cart-count').textContent = totals.itemCount;
    document.getElementById('mobile-cart-count').textContent = totals.itemCount;
    document.getElementById('mobile-cart-total').textContent = formatCurrency(totals.total);
    document.getElementById('mobile-cart-toggle')?.classList.toggle('is-empty', isEmpty);

    // Toggle visibility
    document.getElementById('cart-empty').style.display = isEmpty ? 'block' : 'none';
    document.getElementById('cart-items').style.display = isEmpty ? 'none' : 'block';
    document.getElementById('cart-summary').style.display = isEmpty ? 'none' : 'block';
    document.getElementById('clear-cart-btn').style.display = isEmpty ? 'none' : 'block';
    document.getElementById('cart-continue-btn').style.display = isEmpty ? 'none' : 'block';

    // Update totals
    document.getElementById('cart-subtotal').textContent = formatCurrency(totals.subtotal);
    document.getElementById('cart-tax').textContent = formatCurrency(totals.tax);
    document.getElementById('cart-gratuity').textContent = formatCurrency(totals.gratuity);
    document.getElementById('cart-total').textContent = formatCurrency(totals.total);

    // Render cart items
    renderCartItems();

    // Enable/disable continue button
    document.getElementById('continue-to-payment-btn').disabled = isEmpty;
  }

  function renderCartItems() {
    const container = document.getElementById('cart-items');
    container.innerHTML = '';

    cart.forEach(item => {
      const template = document.getElementById('cart-item-template');
      const clone = template.content.cloneNode(true);
      const cartItem = clone.querySelector('.cart-item');

      cartItem.dataset.itemId = item.id;
      cartItem.querySelector('.cart-item-name').textContent = item.name;
      cartItem.querySelector('.cart-item-price').textContent = formatCurrency(item.price * item.quantity);
      cartItem.querySelector('.cart-item-qty').textContent = item.quantity;

      // Look up full menu item
      const menuItem = menuData?.allItems?.find(m => m.id === item.id);

      // Spice selector — its own radio group ("cart-spice-…") so it
      // doesn't fight with the same item's selector on the menu card
      const spiceContainer = cartItem.querySelector('.cart-item-spice');
      const spiceOptions = menuItem ? getSpiceOptions(menuItem) : [];
      if (spiceOptions.length) {
        if (!spiceOptions.includes(item.spiceLevel)) item.spiceLevel = spiceOptions[0];
        spiceContainer.querySelector('.cart-spice-options').innerHTML = spiceOptions.map(level => `
          <label class="cart-spice-option">
            <input type="radio" name="cart-spice-${escapeHtml(item.id)}" value="${escapeHtml(level)}"${item.spiceLevel === level ? ' checked' : ''}>
            <span class="cart-spice-badge">${escapeHtml(formatSpice(level))}</span>
          </label>
        `).join('');
        spiceContainer.querySelectorAll('input[type="radio"]').forEach(radio => {
          radio.addEventListener('change', () => {
            const cartEntry = cart.find(c => c.id === item.id);
            if (cartEntry) cartEntry.spiceLevel = radio.value;
          });
        });
        spiceContainer.style.display = ''; // layout comes from the stylesheet
      } else {
        spiceContainer.style.display = 'none';
      }

      // Side selector
      const sideContainer = cartItem.querySelector('.cart-item-sides');
      if (menuItem && menuItem.sides && menuItem.sides.length > 0) {
        if (!item.selectedSide) item.selectedSide = menuItem.sides[0];
        sideContainer.innerHTML = `
          <span class="cart-sides-label">Side</span>
          <div class="cart-sides-options">
            ${menuItem.sides.map(side => `
              <label class="cart-side-option">
                <input type="radio" name="cart-side-${escapeHtml(item.id)}" value="${escapeHtml(side)}"${item.selectedSide === side ? ' checked' : ''}>
                <span class="cart-side-badge">${escapeHtml(side)}</span>
              </label>
            `).join('')}
          </div>
        `;
        sideContainer.querySelectorAll('input[type="radio"]').forEach(radio => {
          radio.addEventListener('change', () => {
            const cartEntry = cart.find(c => c.id === item.id);
            if (cartEntry) cartEntry.selectedSide = radio.value;
          });
        });
        sideContainer.style.display = ''; // layout comes from the stylesheet
      } else {
        sideContainer.style.display = 'none';
      }

      container.appendChild(cartItem);
    });
  }

  function updateCartIndicators() {
    document.querySelectorAll('.menu-item-card').forEach(card => {
      const itemId = card.dataset.itemId;
      const cartItem = cart.find(c => c.id === itemId);

      const actions = card.querySelector('.menu-item-actions');
      const inCart = card.querySelector('.menu-item-in-cart');

      if (cartItem) {
        actions.style.display = 'none';
        inCart.style.display = 'flex';
        inCart.querySelector('.in-cart-qty').textContent = cartItem.quantity;
        card.classList.add('in-cart');
      } else {
        actions.style.display = 'flex';
        inCart.style.display = 'none';
        card.classList.remove('in-cart');
      }
    });
  }

  function toggleMobileCart() {
    const sidebar = document.getElementById('cart-sidebar');
    const open = sidebar.classList.toggle('mobile-open');
    document.body.classList.toggle('reservation-cart-open', open);
    if (open) sidebar.querySelector('.cart-close-btn')?.focus();
  }

  function closeMobileCart() {
    const sidebar = document.getElementById('cart-sidebar');
    const wasOpen = sidebar?.classList.contains('mobile-open');
    sidebar?.classList.remove('mobile-open');
    document.body.classList.remove('reservation-cart-open');
    if (wasOpen) document.getElementById('mobile-cart-toggle')?.focus({ preventScroll: true });
  }


  // ============== STEP 4: SUMMARY & PAYMENT ==============

  function populateSummary() {
    // Reservation details
    const guests = getGuestCount();
    document.getElementById('summary-date').textContent = selectedSlot ? formatDateDisplay(selectedSlot.date) : '';
    document.getElementById('summary-time').textContent = selectedSlot ? formatTime(selectedSlot.time) : '';
    document.getElementById('summary-guests').textContent = `${guests} guests`;

    document.getElementById('summary-name').textContent = document.getElementById('reservation-name')?.value || '';
    document.getElementById('summary-email').textContent = document.getElementById('reservation-email')?.value || '';
    document.getElementById('summary-phone').textContent = document.getElementById('reservation-phone')?.value || '';

    // Order items
    const orderContainer = document.getElementById('summary-order-items');
    orderContainer.innerHTML = '';

    cart.forEach(item => {
      const div = document.createElement('div');
      div.className = 'summary-order-item';
      const spiceLabel = item.spiceLevel ? ` <span class="summary-spice-badge">${escapeHtml(formatSpice(item.spiceLevel))}</span>` : '';
      const sideLabel = item.selectedSide ? ` <span class="summary-side-badge">+ ${escapeHtml(item.selectedSide)}</span>` : '';
      div.innerHTML = `
        <span class="summary-item-qty">${item.quantity}×</span>
        <span class="summary-item-name">${escapeHtml(item.name)}${spiceLabel}${sideLabel}</span>
        <span class="summary-item-price">${formatCurrency(item.price * item.quantity)}</span>
      `;
      orderContainer.appendChild(div);
    });

    // Totals
    const totals = getCartTotals();
    document.getElementById('summary-subtotal').textContent = formatCurrency(totals.subtotal);
    document.getElementById('summary-tax').textContent = formatCurrency(totals.tax);
    document.getElementById('summary-gratuity').textContent = formatCurrency(totals.gratuity);
    document.getElementById('summary-total').textContent = formatCurrency(totals.total);

    // The guest agrees to this exact amount — ask again if it changed
    const gratuityAmount = document.getElementById('gratuity-agree-amount');
    const gratuityBox = document.getElementById('accept-gratuity');
    if (gratuityAmount && gratuityAmount.textContent !== formatCurrency(totals.gratuity)) {
      gratuityAmount.textContent = formatCurrency(totals.gratuity);
      if (gratuityBox) gratuityBox.checked = false;
    }

    updateWalletButtons(totals.total);
  }

  function initStripe() {
    if (!config.stripe?.publishableKey) return;

    try {
      stripe = Stripe(config.stripe.publishableKey);
      // Load the site's body font (Asap) inside Stripe's secure card field
      const elements = stripe.elements({
        fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Asap:wght@400;600&display=swap' }]
      });

      cardElement = elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#2c2c2c',
            iconColor: '#3a7d44',
            fontFamily: 'Asap, "Segoe UI", system-ui, sans-serif',
            '::placeholder': { color: '#767676' }
          },
          invalid: {
            color: '#c00a27',
            iconColor: '#c00a27'
          }
        }
      });

      cardElement.mount('#card-element');

      cardElement.on('change', (event) => {
        const errorEl = document.getElementById('card-errors');
        errorEl.textContent = event.error ? event.error.message : '';
      });

      console.log('Stripe initialized');
    } catch (error) {
      console.error('Stripe init error:', error);
    }
  }

  /**
   * Apple Pay / Google Pay buttons. Stripe only shows them on devices that
   * support them. They use the same reservation and payment steps as a card.
   */
  function updateWalletButtons(amount) {
    if (!stripe || !(amount >= 50) || !document.getElementById('express-checkout')) return;

    walletAmount = amount;
    if (walletElements) {
      walletElements.update({ amount: amount });
      return;
    }

    try {
      walletElements = stripe.elements({
        mode: 'payment',
        amount: amount,
        currency: config.stripe.currency || 'usd',
        // Card only, so the buttons are just Apple Pay and Google Pay
        allowedPaymentMethodTypes: ['card']
      });

      const walletButtons = walletElements.create('expressCheckout', {
        buttonHeight: 48,
        // Apple Pay and Google Pay only — Stripe would otherwise add Link
        paymentMethods: { link: 'never', paypal: 'never', amazonPay: 'never', klarna: 'never' }
      });
      walletButtons.mount('#express-checkout');

      // Hidden until Stripe knows this device has a wallet to show
      walletButtons.on('availablepaymentmethodschange', (event) => {
        document.getElementById('wallet-pay').hidden = !event.paymentMethods;
      });
      walletButtons.on('click', onWalletClick);
      walletButtons.on('confirm', onWalletConfirm);
    } catch (error) {
      console.error('Wallet buttons error:', error);
    }
  }

  function onWalletClick(event) {
    // Stripe waits at most 1 second for an answer, so only quick checks here
    if (document.getElementById('confirm-btn').disabled) {
      event.reject();
      return;
    }
    if (pendingReservation?.paid) {
      showMessage('Your payment already went through. Press "Finish your reservation" to finish.', 'error');
      event.reject();
      return;
    }
    if (!validateAgreements()) {
      event.reject();
      return;
    }
    hideMessage();
    event.resolve();
  }

  async function onWalletConfirm(event) {
    let paying = false;
    setConfirmBusy(true);

    try {
      const { error: submitError } = await walletElements.submit();
      if (submitError) throw new Error(submitError.message);

      const reservationData = getReservationData();
      await processPayment(reservationData, (clientSecret, reservation) => {
        if (reservation.totals.total !== walletAmount) {
          // The server priced the reservation differently (e.g. the menu
          // changed), so show the guest the real total before charging it
          updateWalletButtons(reservation.totals.total);
          throw new Error(`Your total is now ${formatCurrency(reservation.totals.total)}. Please press the button again.`);
        }
        paying = true;
        return stripe.confirmPayment({
          elements: walletElements,
          clientSecret: clientSecret,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: {
              billing_details: { name: reservationData.name, email: reservationData.email, phone: reservationData.phone }
            }
          },
          redirect: 'if_required'
        });
      });
    } catch (error) {
      console.error('Error:', error);
      showMessage(error.message || config.messages?.error, 'error');
      setConfirmBusy(false);
    } finally {
      // Close the wallet sheet if Stripe was never asked to charge it
      if (!paying) event.paymentFailed();
    }
  }

  /**
   * The guest must tick every agreement before paying. Shows the first
   * one missing and returns false.
   */
  function validateAgreements() {
    const terms = document.getElementById('accept-terms');
    if (terms && !terms.checked) {
      showMessage(config.messages?.acceptTerms || 'Please accept the cancellation policy', 'error');
      return false;
    }

    const allergyInfo = document.getElementById('accept-allergy-info');
    if (allergyInfo && !allergyInfo.checked) {
      showMessage('Please confirm you have read the allergen and dietary information', 'error');
      return false;
    }

    const gratuity = document.getElementById('accept-gratuity');
    if (gratuity && !gratuity.checked) {
      showMessage(config.messages?.acceptGratuity || 'Please agree to the gratuity', 'error');
      return false;
    }

    const alcoholId = document.getElementById('accept-alcohol-id');
    if (alcoholId && !alcoholId.checked) {
      showMessage(config.messages?.acceptAlcoholId || 'Please accept the alcohol and picture ID policy', 'error');
      return false;
    }

    return true;
  }

  function getReservationData() {
    return {
      name: document.getElementById('reservation-name')?.value.trim(),
      email: document.getElementById('reservation-email')?.value.trim(),
      phone: document.getElementById('reservation-phone')?.value.trim(),
      guestCount: getGuestCount(),
      date: selectedSlot?.date,
      timeSlot: selectedSlot?.time,
      specialRequests: document.getElementById('reservation-requests')?.value || '',
      orderItems: cart,
      totals: getCartTotals()
    };
  }

  function setConfirmBusy(busy) {
    const confirmBtn = document.getElementById('confirm-btn');
    if (!confirmBtn.dataset.label) confirmBtn.dataset.label = confirmBtn.innerHTML;
    confirmBtn.disabled = busy;
    if (busy) {
      confirmBtn.innerHTML = config.messages?.processing || 'Processing…';
    } else {
      confirmBtn.innerHTML = pendingReservation?.paid ? 'Finish your reservation' : confirmBtn.dataset.label;
    }
  }

  async function confirmReservation() {
    if (!validateAgreements()) return;

    setConfirmBusy(true);

    try {
      const reservationData = getReservationData();

      if (config.showPayment && stripe && cardElement) {
        await processPayment(reservationData, clientSecret => stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: reservationData.name,
              email: reservationData.email,
              phone: reservationData.phone
            }
          }
        }));
      } else {
        await submitReservation(reservationData);
      }

    } catch (error) {
      console.error('Error:', error);
      showMessage(error.message || config.messages?.error, 'error');
      setConfirmBusy(false);
    }
  }

  async function postToApi(action, body) {
    const response = await fetch(`${config.reservationApiUrl}?action=${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(body)
    });
    return response.json();
  }

  /**
   * Create the reservation on the server once. Retries (declined card,
   * network error) re-use it, so they never create a second reservation
   * or charge. Once paid, it is always re-used even if the form is edited.
   */
  async function getOrCreateReservation(reservationData) {
    const key = JSON.stringify([
      reservationData.name, reservationData.email, reservationData.phone, reservationData.specialRequests,
      reservationData.guestCount, reservationData.date, reservationData.timeSlot, cart
    ]);
    if (pendingReservation && (pendingReservation.paid || pendingReservation.key === key)) {
      return pendingReservation;
    }

    const result = await postToApi('createReservation', { reservation: reservationData });
    if (!result.success) {
      throw new Error(result.error || 'Failed to create reservation');
    }

    pendingReservation = {
      key: key,
      reservationId: result.data.reservationId,
      totals: result.data.totals || reservationData.totals, // server-priced totals
      paid: false
    };
    return pendingReservation;
  }

  /**
   * confirmWithStripe(clientSecret, reservation) charges the card or wallet
   * and resolves to Stripe's { error, paymentIntent }.
   */
  async function processPayment(reservationData, confirmWithStripe) {

    // STEP 1: Create (or re-use) the reservation — the server checks the
    // time is still free and prices the menu
    const reservation = await getOrCreateReservation(reservationData);

    if (!reservation.paid) {
      // STEP 2: Create (or re-use) the Stripe payment. The server charges
      // the reservation's stored total — no amount is sent from here.
      const intentResult = await postToApi('createPaymentIntent', {
        reservationId: reservation.reservationId
      });

      if (!intentResult.success) {
        throw new Error(intentResult.error || 'Failed to create payment');
      }

      if (intentResult.alreadyPaid) {
        reservation.paid = true;
      } else {
        if (!intentResult.clientSecret) {
          throw new Error('Failed to create payment');
        }

        // STEP 3: Charge the card or wallet with Stripe
        const { error, paymentIntent } = await confirmWithStripe(intentResult.clientSecret, reservation);

        if (error) {
          if (error.payment_intent?.status !== 'succeeded') throw new Error(error.message);
          reservation.paid = true; // already paid on an earlier attempt
        } else if (paymentIntent?.status === 'succeeded') {
          reservation.paid = true;
        } else {
          throw new Error('Your payment was not completed. Please try again.');
        }
      }
    }

    // STEP 4: Confirm on the backend (verifies the payment with Stripe, sends emails)
    const confirmResult = await postToApi('confirmPayment', {
      reservationId: reservation.reservationId
    });

    if (!confirmResult.success) {
      const callUs = config.restaurantPhone ? `, or call us at ${config.restaurantPhone}` : '';
      throw new Error(
        `Your payment went through, but we couldn't finish your reservation (${confirmResult.error || 'unknown error'}). ` +
        `Please press the button again${callUs}. You won't be charged twice.`
      );
    }

    pendingReservation = null;
    showSuccess(reservation.reservationId, reservation.totals, reservationData);
  }

  async function submitReservation(reservationData) {
    // Used when payment is disabled — the reservation stays pending
    // until the restaurant confirms it from the admin panel
    const result = await postToApi('createReservation', { reservation: reservationData });

    if (!result.success) {
      throw new Error(result.error || 'Failed to create reservation');
    }

    showSuccess(result.data.reservationId, result.data.totals || reservationData.totals, reservationData);
  }

  function showSuccess(reservationId, totals, reservationData) {
    closeMobileCart();

    document.getElementById('success-reservation-id').textContent = reservationId || 'PENDING';
    document.getElementById('success-amount').textContent = formatCurrency((totals || getCartTotals()).total);
    document.getElementById('success-when').textContent =
      `${formatDateShort(reservationData.date)}, ${formatTime(reservationData.timeSlot)}`;
    document.getElementById('success-guests').textContent = `${reservationData.guestCount} guests`;

    const emailEl = document.getElementById('success-email');
    if (emailEl && reservationData.email) {
      emailEl.textContent = `We've emailed your confirmation to ${reservationData.email}.`;
    }

    // Show success step
    document.querySelectorAll('.form-step').forEach(el => el.style.display = 'none');
    document.querySelector('[data-form-step="success"]').style.display = 'block';

    // All steps completed
    document.querySelectorAll('.step').forEach(el => el.classList.add('completed'));
    document.querySelectorAll('.step-line').forEach(el => el.classList.add('completed'));

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }


  // ============== UTILITIES ==============

  function showMessage(text, type = 'info') {
    const el = document.getElementById('reservation-message');
    if (!el) return;

    el.textContent = text;
    el.className = `reservation-message ${type}`;
    el.style.display = 'block';
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function hideMessage() {
    const el = document.getElementById('reservation-message');
    if (el) el.style.display = 'none';
  }

  async function getJson(url) {
    const response = await fetch(url);
    return response.json();
  }

  function getGuestCount() {
    return parseInt(document.getElementById('reservation-guests')?.value, 10) || 0;
  }

  function formatCurrency(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: config.stripe?.currency?.toUpperCase() || 'USD'
    }).format(cents / 100);
  }

  // Dates are YYYY-MM-DD strings in restaurant time. They are turned
  // into UTC-midnight dates only for arithmetic and display, so the
  // guest's own time zone never shifts them.

  function dateFromString(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  }

  function addDays(dateStr, days) {
    const date = dateFromString(dateStr);
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
  }

  function getDayOfWeek(dateStr) {
    return dateFromString(dateStr).getUTCDay();
  }

  function localToday() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }

  function formatDateDisplay(dateStr) {
    return dateFromString(dateStr).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC'
    });
  }

  function formatDateShort(dateStr) {
    return dateFromString(dateStr).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC'
    });
  }

  function formatTime(timeStr) {
    const [hours, minutes] = String(timeStr || '').split(':');
    const hour = parseInt(hours, 10);
    if (isNaN(hour)) return String(timeStr || '');
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  }

  /** Open days as short ranges, e.g. "Tue–Sun" */
  function describeOpenDays() {
    const short = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const isOpen = d => !settings.closedDays.includes(d);
    if ([0, 1, 2, 3, 4, 5, 6].every(isOpen)) return 'Every day';

    // Start the week on the first open day after a closed one, so a
    // run like Tue–Sun isn't split at Sunday
    let start = 0;
    while (!(isOpen(start) && !isOpen((start + 6) % 7))) start++;

    const runs = [];
    for (let i = 0; i < 7; i++) {
      const day = (start + i) % 7;
      if (!isOpen(day)) continue;
      const last = runs[runs.length - 1];
      if (last && (last.end + 1) % 7 === day) last.end = day;
      else runs.push({ start: day, end: day });
    }

    return runs.map(r => r.start === r.end ? short[r.start] : `${short[r.start]}–${short[r.end]}`).join(', ');
  }

  function formatTag(tag) {
    const map = {
      'vegetarian': 'Vegetarian',
      'vegan': 'Vegan',
      'gluten-free': 'Gluten free'
    };
    return map[tag] || tag;
  }

  function formatSpice(level) {
    const map = {
      'mild': 'Mild',
      'medium': 'Medium',
      'spicy': 'Spicy',
      'extra-spicy': 'Extra spicy'
    };
    return map[level] || (level.charAt(0).toUpperCase() + level.slice(1));
  }

  /**
   * Spice levels offered for an item. The menu sheet's spice_level
   * column lists them, e.g. "mild,medium,spicy". The first is the default.
   */
  function getSpiceOptions(item) {
    const raw = Array.isArray(item?.spice_level) ? item.spice_level : String(item?.spice_level || '').split(',');
    return raw.map(s => String(s).trim().toLowerCase()).filter(Boolean);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }


  // ============== PUBLIC API ==============

  return {
    init,
    goToStep,
    loadMenu,
    addToCart,
    updateCartItem,
    updateCartItemFromCart,
    removeFromCart,
    clearCart,
    clearFilters,
    toggleMobileCart,
    closeMobileCart,
    confirmReservation
  };

})();

// Initialize
document.addEventListener('DOMContentLoaded', ReservationApp.init);
